import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type JoinRequest = {
  id: string;
  requester_user_id: string;
  requester_email: string;
  message: string;
  created_at: string;
};

/** Pending prośby o dołączenie do zespołu (widoczne tylko dla ownera przez RLS / RPC). */
export const usePendingJoinRequests = (shelterId: string | undefined) => {
  return useQuery({
    queryKey: ["join-requests", shelterId],
    enabled: !!shelterId,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<JoinRequest[]> => {
      const { data, error } = await (supabase as any).rpc("get_pending_team_join_requests", {
        _shelter_id: shelterId!,
      });
      if (error) throw error;
      return (data ?? []) as JoinRequest[];
    },
  });
};

/** Liczba pending join-requestów dla wszystkich schronisk usera (do badge w nawigacji). */
export const useMyShelterJoinRequestsCount = (shelterId: string | undefined) => {
  const q = usePendingJoinRequests(shelterId);
  return q.data?.length ?? 0;
};

/** Tworzy prośbę o dołączenie do zespołu. Insert direct - RLS waliduje. */
export const useRequestTeamInvite = () => {
  return useMutation({
    mutationFn: async ({ shelterId, message }: { shelterId: string; message: string }) => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user) throw new Error("Musisz być zalogowany");
      if (!user.email) throw new Error("Konto nie ma adresu email");
      if (message.trim().length < 20) throw new Error("Wiadomość musi mieć min. 20 znaków");
      if (message.trim().length > 1000) throw new Error("Wiadomość może mieć max 1000 znaków");

      const { error } = await (supabase as any).from("shelter_team_join_requests").insert({
        shelter_id: shelterId,
        requester_user_id: user.id,
        requester_email: user.email,
        message: message.trim(),
        status: "pending",
      });
      if (error) {
        if (error.code === "23505") throw new Error("Już wysłałeś prośbę o dostęp do tego schroniska");
        if (error.code === "23514") throw new Error(error.message ?? "Nie udało się wysłać prośby");
        throw error;
      }
    },
  });
};

/** Akceptuje prośbę: zmienia status i wysyła zaproszenie team-invite na podany email. */
export const useApproveJoinRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId,
      shelterId,
      requesterEmail,
    }: {
      requestId: string;
      shelterId: string;
      requesterEmail: string;
    }) => {
      // Najpierw wyślij zaproszenie (jeśli się nie uda - nie zmieniamy statusu)
      const { data, error } = await supabase.functions.invoke("invite-team-member", {
        body: { shelter_id: shelterId, email: requesterEmail },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      const { error: updErr } = await (supabase as any)
        .from("shelter_team_join_requests")
        .update({ status: "approved" })
        .eq("id", requestId);
      if (updErr) throw updErr;

      return { shelterId };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["join-requests", r.shelterId] });
      qc.invalidateQueries({ queryKey: ["shelter-invites", r.shelterId] });
    },
  });
};

export const useRejectJoinRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, shelterId }: { requestId: string; shelterId: string }) => {
      const { error } = await (supabase as any)
        .from("shelter_team_join_requests")
        .update({ status: "rejected" })
        .eq("id", requestId);
      if (error) throw error;
      return { shelterId };
    },
    onSuccess: (r) => qc.invalidateQueries({ queryKey: ["join-requests", r.shelterId] }),
  });
};
